import config from "../config.json";
import { AccountInfo, AuthenticationResult, IPublicClientApplication, SilentRequest } from "@azure/msal-browser";


export async function logout(instance: IPublicClientApplication, account: AccountInfo): Promise<void> {
    const request: SilentRequest = { ...config.loginRequest, account: account };
    let response: AuthenticationResult | null = null;

    try {
        // Acquire the token silently to get an id_token_hint for a clean logout.
        response = await instance.acquireTokenSilent(request);

        await instance.logoutRedirect({
            idTokenHint: response?.idToken,
            postLogoutRedirectUri: config.msalConfig.auth.redirectUri || window.location.origin
        });
    } catch (error) {
        // Session has already expired (e.g. idle timeout) so silent acquisition fails —
        // but we must STILL clear the local session and send the user to the login page
        // rather than leaving them on a blank/stale screen.
        console.warn("Silent token acquisition failed during logout; redirecting to login.", error);
        await instance.logoutRedirect({
            postLogoutRedirectUri: config.msalConfig.auth.redirectUri || window.location.origin
        });
    }
}

export async function getAccessToken(instance: IPublicClientApplication, account: AccountInfo): Promise<string> {
    const request: SilentRequest = { ...config.loginRequest, account: account };

    try {
        // Handle a redirect response if we're returning from one, otherwise
        // acquire the token silently from the cache / refresh token.
        const redirectResponse = await instance.handleRedirectPromise();
        const response: AuthenticationResult | null =
            redirectResponse ?? await instance.acquireTokenSilent(request);

        if (response?.accessToken) {
            return response.accessToken;
        }

        // No cached/silent token available → re-authenticate.
        await instance.acquireTokenRedirect(request);
    } catch (error) {
        // Any silent-acquisition failure (interaction required, expired session or refresh
        // token, etc.) means the session has effectively ended. Redirect to re-authenticate
        // instead of throwing the error back to the caller — which previously bubbled up to
        // each component's catch block and left the portal showing blank data.
        // acquireTokenRedirect silently refreshes if the B2C session is still alive, or
        // presents the login page if it isn't.
        console.warn("Silent token acquisition failed; redirecting to login.", error);
        await instance.acquireTokenRedirect(request);
    }

    // acquireTokenRedirect triggers a full-page navigation. Block here (rather than
    // returning an empty string) so no caller proceeds to issue a request with a
    // missing bearer token in the brief window before the page unloads.
    return new Promise<string>(() => { });
}
